import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Button, Input } from 'antd';

import fontService, { FontWeight } from 'services/FontService';
import { ICvLocale, Languages } from 'context';
import { ToastTypes } from 'atoms';
import { useSaveCvText, useToaster } from 'core';
import { CvLanguagePicker } from 'molecules';
import { FormFieldset } from 'organisms/ResumeForm';

const { TextArea } = Input;

export interface ICvTextBlock {
  locales: ICvLocale[];
}

const DEFAULT_LANGUAGE = process.env.REACT_APP_LANG === 'pl' ? Languages.polish : Languages.russian;

/**
 * Per-language CV headline (`jobTitle`) + `about` text (spec 0018 §9 — RUS/ENG/POL).
 * Saved independently of the language-neutral profile via `saveCvText`.
 */
export const CvTextBlock = memo(({ locales }: ICvTextBlock) => {
  const { t } = useTranslation();
  const [language, setLanguage] = useState<Languages>(DEFAULT_LANGUAGE);
  const [jobTitle, setJobTitle] = useState('');
  const [about, setAbout] = useState('');
  const [save, { loading, success, error }] = useSaveCvText();
  const { addToast } = useToaster();

  useEffect(() => {
    const locale = locales.find((item) => item.language === language);
    setJobTitle(locale?.jobTitle ?? '');
    setAbout(locale?.about ?? '');
  }, [language, locales]);

  useEffect(() => {
    if (success) {
      addToast('cv_text', { type: ToastTypes.success, text: `${t('careerProfile.cvText.saved')}` });
    }
  }, [success, addToast, t]);

  useEffect(() => {
    if (error) {
      addToast('cv_text', { type: ToastTypes.danger, text: error });
    }
  }, [error, addToast]);

  return (
    <FormFieldset groupTitle={t('careerProfile.cvText.title')}>
      <Info>{t('careerProfile.cvText.info')}</Info>

      <CvLanguagePicker value={language} onChange={setLanguage} />

      <Label>{t('careerProfile.cvText.jobTitle')}</Label>
      <Input
        value={jobTitle}
        maxLength={150}
        placeholder={t('careerProfile.cvText.jobTitlePlaceholder')}
        onChange={({ target: { value } }) => setJobTitle(value)}
      />

      <Label>{t('careerProfile.cvText.about')}</Label>
      <TextArea
        value={about}
        rows={8}
        maxLength={1500}
        placeholder={t('careerProfile.cvText.aboutPlaceholder')}
        onChange={({ target: { value } }) => setAbout(value)}
      />

      <SaveRow>
        <Button type="primary" loading={loading} onClick={() => save(language, jobTitle, about)}>
          {t('careerProfile.cvText.save')}
        </Button>
      </SaveRow>
    </FormFieldset>
  );
});

const Info = styled.p`
  margin: 0 0 16px;
  ${fontService.text({ size: 14, lineHeight: 145, weight: FontWeight.regular })}
`;

const Label = styled.p`
  margin: 20px 0 4px;
  ${fontService.text({ size: 14, lineHeight: 145, weight: FontWeight.medium })}
`;

const SaveRow = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`;
